import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, Wand2, Lock, Users, Plane, Hotel,
  CreditCard, MailCheck, Send, Star, Check, ShieldCheck, Globe2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { apiFetch, api } from "@/lib/api";
import { renderMarkdown } from "@/lib/md";
import FlightResults from "@/components/FlightResults";
import { getReferrerCode, getPartnerSlug } from "@/lib/useReferralTracker";
import { BACKEND_URL } from "@/lib/api";

const FEATURES = [
  { Icon: Sparkles, title: "AI Concierge",
    body: "An always-on points strategist that knows your real balances and searches the live web for award availability and transfer bonuses. Ask in plain English; book in three clicks." },
  { Icon: MailCheck, title: "Weekly Brief",
    body: "Every Monday, a tailored digest of the highest-value moves available to you this week — transfer bonuses ending, expiring credits, sweet spots that just opened up." },
  { Icon: Plane, title: "Flight Planner",
    body: "Tell us where and when. We rank three redemption paths with cents-per-point math, exact taxes, and step-by-step booking instructions on the carrier site." },
  { Icon: Hotel, title: "Hotel & Cert Tracker",
    body: "Free-night certs, GOH awards, suite upgrades, anniversary nights — all in one timeline, sorted by what expires first." },
  { Icon: Users, title: "Household Aware",
    body: "Track your balances and your partner's separately. Plan trips that pool both wallets — with a clear view of who's flying on whose miles." },
  { Icon: CreditCard, title: "Statement Credits",
    body: "Bilt cash, Amex airline incidentals, Plat dining — surfaced before they expire, with a one-click reminder to use them up." },
];

const FAQS = [
  { q: "How is this different from a spreadsheet?",
    a: "A spreadsheet doesn't know that the Marriott→Iberia transfer bonus ends Friday. Or that your Hyatt cert expires in 19 days. Or that Air Canada Aeroplan now charges 25% less for JFK→Lisbon than United does. Reservoir watches all of that for you, every day, and tells you what to do." },
  { q: "Do you actually book flights for me?",
    a: "Not yet — there's no public API for booking award redemptions. We get you to the right page on the airline's site with the exact search params filled in, and we tell you exactly what to click. Most users finish a booking in under three minutes from the Concierge's instructions." },
  { q: "What about my privacy?",
    a: "We never see your credit card numbers or banking details — full stop. Reservoir only ever sees balances and tier statuses. Your conversation with the Concierge is not used to train any AI model." },
  { q: "How accurate are the balances?",
    a: "Refreshed daily by default. Most carriers update within 24 hours of any earning or burning activity. You can also force-refresh any time." },
  { q: "Can I track my partner's accounts?",
    a: "Yes — household tracking is built in. Partners can have their own balances, statuses, and certs, and the Concierge plans trips against both wallets when relevant." },
  { q: "Why 'Reservoir AI'?",
    a: "Because that's what your loyalty currency is — a reservoir most people forget they're sitting on. The 'AI' is the strategist that helps you actually draw from it." },
];

// Snap to next upcoming Monday (or today + 7 if today is Monday).
function nextMondayIso() {
  const d = new Date();
  const days = ((8 - d.getDay()) % 7) || 7;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const CABIN_LABELS = {
  economy:  "Economy",
  premium:  "Premium economy",
  business: "Business",
  first:    "First",
};

const DEMO_DEFAULTS = {
  origin:     "JFK",
  dest:       "NRT",
  week_start: nextMondayIso(),
  cabin:      "business",
};


export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hash } = useLocation();

  // Lifted so a click on "Request invite to book" in the demo can pre-fill
  // the waitlist textarea further down the page.
  const [waitlistReason, setWaitlistReason] = useState("");

  // Show an "Invited by <name>" banner when a valid referral code is
  // present. Lookup is public and silently fails on stale codes.
  const [inviter, setInviter] = useState(null);
  useEffect(() => {
    const code = getReferrerCode();
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/referrals/inviter/${encodeURIComponent(code)}`);
        if (!cancelled && data?.name) setInviter({ name: data.name });
      } catch {
        /* invalid or stale code — skip the banner */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Influencer / partner referral: when a visitor lands via /r/<slug> we
  // fetch the partner's display name + logo and render the logo on the
  // right of the Nav next to the Open Dashboard / Sign in CTA. The
  // partner fetch also writes a partner_clicks row server-side for the
  // funnel counter — happens once per page load.
  const [partner, setPartner] = useState(null);
  useEffect(() => {
    const slug = getPartnerSlug();
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/partners/by-slug/${encodeURIComponent(slug)}`);
        if (!cancelled && data?.name) setPartner(data);
      } catch {
        /* invalid or archived slug — skip the banner */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startSignIn = () => {
    navigate("/login");
  };

  // External links like https://myreservoir.ai/#waitlist need an explicit
  // scrollIntoView — the browser's native hash scroll fires before the SPA
  // has mounted the target section. Re-runs on hash change so in-page
  // anchor clicks keep working too.
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;
    // Wait two frames so the section's final layout (images, fonts) is
    // settled before we measure the scroll target.
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="min-h-screen" data-testid="landing-page" style={{ background: "#fafaf7" }}>
      <Nav user={user} onSignIn={startSignIn} onOpen={() => navigate("/dashboard")} partner={partner} />
      {inviter && (
        <div
          className="max-w-[1180px] mx-auto px-6 pt-4"
          data-testid="landing-invited-by-banner"
        >
          <div className="inline-flex items-center gap-2 border border-emerald-200 bg-emerald-50/80 text-emerald-900 rounded-full px-3 py-1.5 text-[12.5px]">
            <span className="font-semibold">Invited by {inviter.name}</span>
            <span className="text-emerald-800/80">·</span>
            <button
              type="button"
              onClick={startSignIn}
              className="font-semibold underline underline-offset-2 hover:text-emerald-950"
            >
              Sign in to claim your invitation
            </button>
          </div>
        </div>
      )}
      <Hero onSignIn={startSignIn} />
      <Strip />
      <HowItWorks />
      <DemoSection onRequestInvite={setWaitlistReason} />
      <FeatureGrid />
      <Privacy />
      <FounderNote />
      <FAQ />
      <Waitlist reason={waitlistReason} setReason={setWaitlistReason} />
      <Footer />
    </div>
  );
}


// -------------------- Nav --------------------
function Nav({ user, onSignIn, onOpen, partner }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const logoSrc = partner?.logo_url
    ? `${BACKEND_URL}${partner.logo_url}`
    : null;
  return (
    <header
      className={`sticky top-0 z-40 transition-colors ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
      data-testid="landing-nav"
    >
      <div className="max-w-[1180px] mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5" data-testid="brand-mark">
          <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center text-white font-bold font-display text-sm">R</div>
          <span className="font-display font-semibold tracking-tight">
            Reservoir <span className="text-neutral-400 font-medium">AI</span>
          </span>
        </a>
        <nav className="hidden md:flex items-center gap-7 text-[13px] text-neutral-700">
          <a href="#how" className="hover:text-black">How it works</a>
          <a href="#demo" className="hover:text-black">Demo</a>
          <a href="#features" className="hover:text-black">Features</a>
          <a href="#privacy" className="hover:text-black">Privacy</a>
          <a href="#faq" className="hover:text-black">FAQ</a>
        </nav>
        <div className="flex items-center gap-3">
          {user ? (
            <button onClick={onOpen} className="btn btn-primary text-[12.5px]" data-testid="open-dashboard-btn">
              Open dashboard <ArrowRight size={12} />
            </button>
          ) : (
            <>
              <a href="#waitlist" className="text-[13px] text-neutral-700 hover:text-black hidden sm:inline">Join Waitlist</a>
              <button onClick={onSignIn} className="btn btn-primary text-[12.5px]" data-testid="nav-signin-btn">
                Sign in
              </button>
            </>
          )}
          {logoSrc && (
            <span
              className="hidden sm:inline-flex items-center gap-2 pl-3 ml-1 border-l border-black/10"
              title={`Invited by ${partner.name}`}
              data-testid="landing-partner-logo"
            >
              <img
                src={logoSrc}
                alt={`${partner.name} logo`}
                className="h-7 w-auto max-w-[120px] object-contain rounded-sm"
              />
            </span>
          )}
        </div>
      </div>
    </header>
  );
}


// -------------------- Hero --------------------
// Square calendar-style tile with an internal 0–9 reel. The strip translates
// up as the parent's value ticks, so each digit position scrolls smoothly
// rather than blinking between values.
function DigitTile({ digit }) {
  const n = Math.max(0, Math.min(9, parseInt(digit, 10) || 0));
  return (
    <div className="relative bg-white border border-black/10 rounded-lg shadow-sm overflow-hidden w-[54px] h-[54px] md:w-[74px] md:h-[74px]">
      <div
        className="absolute inset-x-0 top-0 h-[1000%] flex flex-col"
        style={{
          transform: `translateY(-${n * 10}%)`,
          transition: "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {Array.from({ length: 10 }, (_, i) => (
          <div
            key={i}
            className="h-[10%] flex items-center justify-center font-display font-extrabold tabular text-black text-[34px] md:text-[48px] leading-none"
          >
            {i}
          </div>
        ))}
      </div>
    </div>
  );
}

function PointsCounter() {
  const [target, setTarget] = useState(null);
  const [display, setDisplay] = useState(1);

  useEffect(() => {
    let cancelled = false;
    api.get("/stats/points").then(({ data }) => {
      if (!cancelled) setTarget(Number(data?.total) || 0);
    }).catch(() => {
      if (!cancelled) setTarget(0);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (target == null || target < 1) return;
    const duration = 2200;
    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = Math.max(1, Math.floor(1 + eased * (target - 1)));
      setDisplay(v);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  if (target == null || target < 1) return null;

  const digits = String(display).split("");
  return (
    <div
      className="flex flex-col items-center gap-3 rise-in"
      style={{ animationDelay: "60ms" }}
      data-testid="hero-points-counter"
    >
      <div className="flex items-center gap-1 md:gap-1.5">
        {digits.map((d, i) => <DigitTile key={i} digit={d} />)}
      </div>
      <div className="text-[11px] md:text-[12.5px] font-semibold uppercase tracking-[0.18em] text-neutral-500">
        Points / Miles tracked
      </div>
    </div>
  );
}


function Hero({ onSignIn }) {
  return (
    <section id="top" className="pt-16 pb-20 lg:pt-24 lg:pb-28">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="inline-flex items-center gap-2 pill pill-neutral mb-6 rise-in">
          <Star size={11} /> PRIVATE BETA · INVITE-ONLY
        </div>
        <h1
          className="font-display font-extrabold tracking-tighter leading-[0.95] text-[44px] md:text-[64px] lg:text-[84px] max-w-[900px] rise-in"
          style={{ animationDelay: "60ms" }}
        >
          Your points,<br /> finally working for you.
        </h1>
        <p
          className="text-neutral-700 text-[17px] md:text-[19px] leading-relaxed mt-7 max-w-[640px] rise-in"
          style={{ animationDelay: "140ms" }}
        >
          You've earned the miles. You've stockpiled the points. You're still
          paying cash for hotels. Reservoir keeps every loyalty currency you
          own in one place — and a Concierge that knows your balances tells
          you exactly when, where, and how to burn them.
        </p>
        <div className="mt-10 flex items-center gap-3 flex-wrap rise-in" style={{ animationDelay: "220ms" }}>
          <button onClick={onSignIn} className="btn btn-primary text-[14px] px-5 py-3" data-testid="hero-signin-btn">
            Sign in with Google <ArrowRight size={14} />
          </button>
          <a href="#waitlist" className="btn btn-ghost text-[14px] px-5 py-3">
            Join Waitlist
          </a>
          <span className="text-[12.5px] text-neutral-500 ml-2">Free during private beta · no card required.</span>
        </div>

        {/* Hero visual: counter + stylized dashboard preview */}
        <div className="mt-14 relative">
          <div className="flex justify-center mb-8">
            <PointsCounter />
          </div>
          <DashboardPreview />
        </div>
      </div>
    </section>
  );
}


function DashboardPreview() {
  const tiles = [
    { label: "Hotel points", value: "511,481", sub: "Hyatt · Marriott · IHG" },
    { label: "Airline miles", value: "44,634", sub: "Delta · Emirates · Southwest" },
    { label: "Transferable", value: "499,077", sub: "Cap One + Amex + Bilt" },
    { label: "Cash & credits", value: "$950", sub: "3 items expiring soon" },
  ];
  return (
    <div className="card p-6 lg:p-8 fade-in" style={{ animationDelay: "320ms" }}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t, i) => (
          <div key={t.label} className="card p-5 rise-in" style={{ animationDelay: `${380 + i * 70}ms` }}>
            <div className="section-eyebrow">{t.label}</div>
            <div className="font-display tabular text-[26px] font-extrabold tracking-tight mt-2">{t.value}</div>
            <div className="text-[12px] text-neutral-500 mt-1 truncate">{t.sub}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <div className="section-eyebrow flex items-center gap-2"><Sparkles size={11}/> Reservoir Brief</div>
          <div className="font-display text-lg font-bold mt-1">Mihir's Points Intel · Week of May 4</div>
          <div className="text-[13px] text-neutral-700 mt-2 leading-relaxed">
            <b>Top move:</b> Transfer 60k Cap One → Aeroplan for JFK→LIS in business
            on TAP (~2.0 cpp, 50% less than United's award chart). The transfer
            is instant; book within 4 days while space holds.
          </div>
        </div>
        <div className="card p-5">
          <div className="section-eyebrow flex items-center gap-2"><Wand2 size={11}/> Use this now</div>
          <div className="space-y-1.5 mt-2 text-[13px]">
            <Row pill="urgent" cat="CARD" label="Bilt Hotel Credit" sub="$200 · expires Jun 30" />
            <Row pill="due" cat="HOTEL" label="Hyatt Cat 1-4 Free Night" sub="× 1 · expires Aug 30" />
            <Row pill="safe" cat="HOTEL" label="Hyatt Guest of Honor" sub="× 4 · expires Feb 28, 2027" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ pill, cat, label, sub }) {
  const cls = pill === "urgent" ? "pill-bad" : pill === "due" ? "pill-warn" : "pill-good";
  return (
    <div className="flex items-center gap-2 px-3 py-2 border border-black/5 rounded-[10px]">
      <span className={`pill ${cls} shrink-0`}>{pill}</span>
      <span className="text-[10.5px] font-semibold tracking-wider text-neutral-500 px-1.5 py-0.5 rounded bg-black/[0.04]">{cat}</span>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[13px] truncate">{label}</div>
        <div className="text-[11.5px] text-neutral-500 truncate">{sub}</div>
      </div>
    </div>
  );
}


// -------------------- Logo strip / one-liner --------------------
const STRIP_BRANDS = [
  { name: "Hyatt",       logo: "/logos/hyatt.png" },
  { name: "Marriott",    logo: "/logos/Marriott.png" },
  { name: "IHG",         logo: "/logos/ihg.png" },
  { name: "Hilton",      logo: "/logos/hilton.png" },
  { name: "United",      logo: "/logos/United.png" },
  { name: "Delta",       logo: "/logos/delta.png" },
  { name: "American",    logo: "/logos/american.png" },
  { name: "Emirates",    logo: "/logos/emirates.png" },
  { name: "Chase",       logo: "/logos/chaseink.png" },
  { name: "Amex",        logo: "/logos/amex.jpeg" },
  { name: "Capital One", logo: "/logos/capone.avif" },
  { name: "Bilt",        logo: "/logos/Bilt.png" },
];

function Strip() {
  return (
    <section className="border-y border-black/5 bg-white py-10">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 text-center mb-6">
          Tracks every program you actually use
        </div>
        <div
          className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5"
          data-testid="landing-logo-strip"
        >
          {STRIP_BRANDS.map((b) =>
            b.logo ? (
              <img
                key={b.name}
                src={b.logo}
                alt={b.name}
                className="h-7 md:h-8 w-auto object-contain opacity-80 grayscale hover:grayscale-0 hover:opacity-100 transition duration-200"
                loading="lazy"
              />
            ) : (
              <span
                key={b.name}
                className="text-neutral-500 text-[13px] font-semibold tracking-tight"
              >
                {b.name}
              </span>
            )
          )}
          <span className="text-neutral-400 text-[12.5px]">+ 100 more</span>
        </div>
      </div>
    </section>
  );
}


// -------------------- How it works --------------------
function HowItWorks() {
  const steps = [
    {
      num: "01",
      title: "Sign up in 30 seconds",
      body: "One click with Google. Reservoir spins up your private dashboard automatically — no carrier passwords, no spreadsheet imports.",
    },
    {
      num: "02",
      title: "Connect your loyalty programs",
      body: "Add programs from a curated catalog. We keep balances and elite statuses fresh in the background, so the dashboard always reflects reality.",
    },
    {
      num: "03",
      title: "Plan trips, redeem smartly",
      body: "Ask the AI Concierge in plain English. It reads your real balances, searches live web for award availability and transfer bonuses, and walks you to the booking page.",
    },
  ];
  return (
    <section id="how" className="py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="section-eyebrow">How it works</div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-2 max-w-2xl">
          Three steps. Zero spreadsheets.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={s.num} className="card p-7 rise-in" style={{ animationDelay: `${i * 100}ms` }}>
              <div className="font-display tabular text-[36px] font-extrabold text-neutral-300 leading-none">{s.num}</div>
              <h3 className="font-display font-bold text-xl tracking-tight mt-4">{s.title}</h3>
              <p className="text-[14px] text-neutral-700 leading-relaxed mt-2">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// -------------------- Live Concierge demo --------------------

// Snap any picked date to the Monday of that ISO week (display only — the
// server re-normalizes too).
function snapToMondayIso(iso) {
  if (!iso) return iso;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getDay(); // 0 Sun ... 6 Sat
  const offset = day === 0 ? -6 : (1 - day); // Mon=0 offset
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function formatWeekRange(weekStartIso) {
  if (!weekStartIso) return "";
  const start = new Date(`${weekStartIso}T00:00:00`);
  if (Number.isNaN(start.getTime())) return "";
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opt = { month: "short", day: "numeric" };
  const left = start.toLocaleDateString(undefined, opt);
  const right = end.toLocaleDateString(undefined, opt);
  return `${left} – ${right}, ${end.getFullYear()}`;
}

function DemoSection({ onRequestInvite }) {
  const [form, setForm] = useState(DEMO_DEFAULTS);
  const [running, setRunning] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [flights, setFlights] = useState([]);
  const [query, setQuery] = useState(null);
  const [err, setErr] = useState("");
  const [remaining, setRemaining] = useState(null);
  const abortRef = useRef(null);

  const inviteReason = useMemo(() => {
    if (!query) return "";
    const range = formatWeekRange(query.start_date);
    return `Interested in: ${query.origin} → ${query.destination}, ${CABIN_LABELS[query.cabin] || query.cabin}, week of ${range}.`;
  }, [query]);

  const submit = async (e) => {
    e?.preventDefault();
    if (running) return;
    setErr("");
    setNarrative("");
    setFlights([]);
    setQuery(null);
    setRemaining(null);
    setRunning(true);
    const controller = new AbortController();
    abortRef.current = controller;

    const payload = {
      origin:     (form.origin || "").trim().toUpperCase(),
      dest:       (form.dest || "").trim().toUpperCase(),
      week_start: snapToMondayIso(form.week_start),
      cabin:      form.cabin,
    };

    try {
      const resp = await apiFetch("/demo/plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error(await resp.text());

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const evt = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!evt.startsWith("data:")) continue;
          try {
            const obj = JSON.parse(evt.slice(5).trim());
            if (obj.flights) {
              setFlights(obj.flights);
              setQuery(obj.query || null);
            }
            if (obj.text) setNarrative((p) => p + obj.text);
            if (obj.remaining !== undefined) setRemaining(obj.remaining);
            if (obj.error) setErr(obj.message || obj.error);
          } catch {}
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setErr(String(e.message || e));
    } finally {
      setRunning(false);
    }
  };

  const handleInviteClick = (e) => {
    // Default <a href="#waitlist"> behavior carries the scroll; we just need
    // to pre-fill the waitlist reason before navigation.
    if (inviteReason) onRequestInvite(inviteReason);
    // Allow the anchor to scroll naturally.
    void e;
  };

  return (
    <section id="demo" className="py-20 lg:py-28 bg-white border-y border-black/5 scroll-mt-20">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="section-eyebrow">Try the Concierge</div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-2 max-w-3xl">
          Throw a real trip at it. Right now.
        </h2>
        <p className="text-neutral-700 text-[15px] mt-4 max-w-2xl leading-relaxed">
          Pick a route and a week. The Concierge searches live award
          availability across every major program and tells you which one
          to use — with the math already done.
        </p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
          <form onSubmit={submit} className="card p-6 space-y-4 self-start">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="label mb-1">Origin (IATA)</div>
                <input
                  className="input"
                  value={form.origin}
                  onChange={(e) => setForm({ ...form, origin: e.target.value.toUpperCase().slice(0, 8) })}
                  placeholder="JFK"
                  data-testid="demo-origin"
                />
              </label>
              <label className="block">
                <div className="label mb-1">Destination (IATA)</div>
                <input
                  className="input"
                  value={form.dest}
                  onChange={(e) => setForm({ ...form, dest: e.target.value.toUpperCase().slice(0, 8) })}
                  placeholder="NRT"
                  data-testid="demo-dest"
                />
              </label>
              <label className="block">
                <div className="label mb-1">Week of</div>
                <input
                  type="date"
                  className="input"
                  value={form.week_start}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setForm({ ...form, week_start: e.target.value })}
                  data-testid="demo-week"
                />
                <div className="text-[11px] text-neutral-500 mt-1">
                  {formatWeekRange(snapToMondayIso(form.week_start)) || "Pick any day; we'll search that Mon – Sun."}
                </div>
              </label>
              <label className="block">
                <div className="label mb-1">Cabin</div>
                <select
                  className="input"
                  value={form.cabin}
                  onChange={(e) => setForm({ ...form, cabin: e.target.value })}
                  data-testid="demo-cabin"
                >
                  <option value="economy">Economy</option>
                  <option value="premium">Premium economy</option>
                  <option value="business">Business</option>
                  <option value="first">First</option>
                </select>
              </label>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-black/5">
              <div className="text-[12px] text-neutral-500">
                {remaining !== null && <span>{remaining} demo run{remaining === 1 ? "" : "s"} left today</span>}
              </div>
              <button type="submit" disabled={running} className="btn btn-primary text-[13px]" data-testid="demo-submit">
                <Send size={12} /> {running ? "Searching…" : "Find awards"}
              </button>
            </div>
          </form>

          <div className="card p-6 min-h-[360px] relative">
            {err && (
              <div className="p-3 rounded-[10px] border text-[13px]"
                   style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>
                {err}
              </div>
            )}
            {!flights.length && !narrative && !err && !running && (
              <div className="flex flex-col items-center justify-center h-full text-center text-neutral-500 py-12">
                <Sparkles size={20} className="mb-3 text-neutral-400" />
                <div className="text-[13px] max-w-xs">
                  Top 5 award options + a strategic note appear here. Live data — no signup needed.
                </div>
              </div>
            )}
            {running && !flights.length && (
              <div className="flex items-center gap-3 text-neutral-600 text-[13px]">
                <span className="spinner" /> Searching live award availability…
              </div>
            )}
            {flights.length > 0 && (
              <FlightResults
                flights={flights}
                query={query}
                hideRoute
                hideFooterNote
                bookRender={() => (
                  <a
                    href="#waitlist"
                    onClick={handleInviteClick}
                    className="btn btn-primary text-[12px] inline-flex items-center gap-1 whitespace-nowrap"
                    data-testid="demo-book-now"
                  >
                    Book Now <ArrowRight size={11} />
                  </a>
                )}
              />
            )}
            {narrative && (
              <div
                className="md text-[13.5px] leading-relaxed mt-5"
                data-testid="demo-result"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(narrative) }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


// -------------------- Feature grid --------------------
function FeatureGrid() {
  return (
    <section id="features" className="py-20 lg:py-28">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="section-eyebrow">What's inside</div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-2 max-w-3xl">
          A dashboard that actually thinks.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.Icon;
            return (
              <div key={f.title} className="card p-6 rise-in" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-9 h-9 rounded-[10px] bg-black text-white flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <h3 className="font-display font-bold text-[17px] mt-4 tracking-tight">{f.title}</h3>
                <p className="text-[13.5px] text-neutral-700 leading-relaxed mt-2">{f.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


// -------------------- Privacy --------------------
function Privacy() {
  const items = [
    { Icon: Lock, t: "Card numbers, never.", b: "We don't store, see, or process credit card numbers — full stop. Reservoir reads balances, not financial accounts." },
    { Icon: ShieldCheck, t: "Bank-grade encryption. Your data is locked down.", b: "Every loyalty login is sealed with AES-256-GCM under a unique key per record, wrapped by AWS KMS in a FIPS 140-2 Level 3 HSM. Passwords transmitted to providers use RSA-OAEP-2048 — so a database breach reveals zero usable credentials." },
    { Icon: Globe2, t: "Your data stays yours.", b: "Conversations with the Concierge are not used to train any model. Export everything, anytime, in one click." },
  ];
  return (
    <section id="privacy" className="py-20 lg:py-28 bg-white border-y border-black/5">
      <div className="max-w-[1180px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12 items-start">
        <div>
          <div className="section-eyebrow">Privacy by default</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2">
            Quietly opinionated about what we touch.
          </h2>
          <p className="text-[14px] text-neutral-700 leading-relaxed mt-4">
            We built Reservoir for ourselves, then for our partners.
            That meant treating "what data we hold" as a design constraint,
            not a checkbox.
          </p>
        </div>
        <div className="md:col-span-2 space-y-4">
          {items.map(({ Icon, t, b }) => (
            <div key={t} className="card p-5 flex gap-4">
              <div className="w-9 h-9 rounded-[10px] bg-black text-white flex items-center justify-center shrink-0">
                <Icon size={16} />
              </div>
              <div>
                <div className="font-display font-bold text-[15px] tracking-tight">{t}</div>
                <div className="text-[13.5px] text-neutral-700 leading-relaxed mt-1">{b}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// -------------------- Founder note --------------------
function FounderNote() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="section-eyebrow">Why we built this</div>
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2">
          Most loyalty programs reward people who think about loyalty programs.
        </h2>
        <p className="text-[15.5px] text-neutral-800 leading-relaxed mt-5">
          The serious points-and-miles community spends hours every week
          tracking transfer bonuses, decoding award charts, and watching for
          the moment a 60k-mile flight becomes 38k. Most travelers — even
          smart, frequent ones — just leave that value on the table.
        </p>
        <p className="text-[15.5px] text-neutral-800 leading-relaxed mt-4">
          Reservoir is what happens when you give the second group access to
          the first group's brain. We track every program you have. We watch
          the deals, the certs, the credits. And when you want to actually go
          somewhere, we hand you a plan with the math already done.
        </p>
        <p className="text-[15.5px] text-neutral-800 leading-relaxed mt-4">
          Free during private beta. Built by travelers, for travelers.
        </p>
      </div>
    </section>
  );
}


// -------------------- FAQ --------------------
function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-20 lg:py-28 bg-white border-y border-black/5 scroll-mt-20">
      <div className="max-w-[820px] mx-auto px-6">
        <div className="section-eyebrow">FAQ</div>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight mt-2">
          Questions, answered.
        </h2>
        <div className="mt-10 divide-y divide-black/10 border-t border-b border-black/10">
          {FAQS.map((f, i) => (
            <button
              key={f.q}
              onClick={() => setOpen(open === i ? -1 : i)}
              className="block w-full text-left py-5"
              data-testid={`faq-${i}`}
            >
              <div className="flex items-start justify-between gap-6">
                <span className="font-display font-bold text-[16px] tracking-tight">{f.q}</span>
                <span className={`text-neutral-400 mt-0.5 transition-transform ${open === i ? "rotate-45" : ""}`}>+</span>
              </div>
              {open === i && (
                <p className="text-[14px] text-neutral-700 leading-relaxed mt-3 max-w-[700px]">{f.a}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}


// -------------------- Waitlist --------------------
function Waitlist({ reason, setReason }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const ref_code = getReferrerCode() || undefined;
      const { data } = await api.post("/waitlist", { email, source: "landing", reason, ref_code });
      setDone(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong, try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="py-20 lg:py-28 scroll-mt-20">
      <div className="max-w-[1180px] mx-auto px-6">
        <div className="card p-8 lg:p-10" style={{ background: "linear-gradient(180deg, #0a0a0a 0%, #1f1f1f 100%)", color: "#fafaf7" }}>
          <div className="section-eyebrow" style={{ color: "#a3a3a3" }}>Join Waitlist</div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight mt-2 leading-tight">
            Quietly building. Inviting from the list.
          </h2>
          <p className="text-neutral-300 text-[14.5px] leading-relaxed mt-3 max-w-md">
            We're letting people in slowly so we can talk to each one. Drop
            your email and a sentence on what you'd want most — we'll reach
            out as spots open up.
          </p>

          {done ? (
            <div className="mt-6 p-4 rounded-[10px]" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <div className="flex items-center gap-2 font-semibold">
                <Check size={16} /> {done.already_on_list ? "You're already on the list — we'll be in touch." : "You're in. We'll be in touch."}
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="waitlist-form">
              <input
                required
                type="email"
                placeholder="you@something.com"
                className="w-full px-4 py-3 rounded-[10px] text-[14px] bg-white/[0.06] border border-white/10 text-white placeholder:text-white/40 outline-none focus:border-white/40 transition-colors"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email"
              />
              <textarea
                rows={2}
                placeholder="What would you want Reservoir to solve for you? (optional)"
                className="w-full px-4 py-3 rounded-[10px] text-[14px] bg-white/[0.06] border border-white/10 text-white placeholder:text-white/40 outline-none focus:border-white/40 transition-colors resize-none"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="waitlist-reason"
              />
              {err && <div className="text-[12.5px]" style={{ color: "#fca5a5" }}>{err}</div>}
              <button
                type="submit"
                disabled={submitting}
                className="btn text-[13px] px-5 py-3 bg-white text-black hover:bg-neutral-200"
                data-testid="waitlist-submit"
              >
                {submitting ? "Sending…" : "Request access"} <ArrowRight size={13} />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}


// -------------------- Footer --------------------
function Footer() {
  return (
    <footer className="py-10 border-t border-black/5">
      <div className="max-w-[1180px] mx-auto px-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-[12.5px] text-neutral-500">
          <div className="w-6 h-6 rounded-md bg-black flex items-center justify-center text-white font-bold text-[10px]">R</div>
          <span>Reservoir AI · myreservoir.ai · made for travelers</span>
        </div>
        <div className="text-[12.5px] text-neutral-500 flex items-center gap-5">
          <a href="#privacy" className="hover:text-black">Privacy</a>
          <a href="#faq" className="hover:text-black">FAQ</a>
          <a href="#waitlist" className="hover:text-black">Join Waitlist</a>
        </div>
      </div>
    </footer>
  );
}
